import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import ManagementPageSkeleton from '../layout/ManagementPageSkeleton';
import RoleService from '../../services/roleService';
import { getRoleName } from '../../utils/roleTranslations';
import ConfirmDialog from '../common/ConfirmDialog';
import RoleEditModal from '../roles/RoleEditModal';
import usePermissions from '../../hooks/usePermissions';
import { Permissions } from '../../constants/Permissions';

const RolesPanel = ({ storeId }) => {
  const { t, i18n } = useTranslation();
  const { hasPermission } = usePermissions(storeId);

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const loadRoles = async () => {
    if (!storeId) {
      setError(t('roles.error.noStoreSelected'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const rolesData = await RoleService.getRolesByStore();
      setRoles(rolesData);
    } catch (err) {
      console.error('Failed to load roles:', err);
      setError(t('roles.error.loading'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const handleCreate = () => {
    setIsCreating(true);
    setSelectedRole(null);
    setEditModalOpen(true);
  };

  const handleEdit = (role) => {
    setIsCreating(false);
    setSelectedRole(role);
    setEditModalOpen(true);
  };

  const handleDelete = (role) => {
    setSelectedRole(role);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedRole || !storeId) return;

    try {
      await RoleService.deleteRole(selectedRole.id);
      await loadRoles();
      setDeleteDialogOpen(false);
      setSelectedRole(null);
    } catch (err) {
      console.error('Failed to delete role:', err);
      if (err.response?.data?.message?.includes('assigned')) {
        setError(t('roles.delete.inUse'));
      } else {
        setError(t('roles.modal.error.delete'));
      }
    }
  };

  const handleModalClose = () => {
    setEditModalOpen(false);
    setSelectedRole(null);
    setIsCreating(false);
  };

  const handleModalSuccess = () => {
    loadRoles();
    handleModalClose();
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <ManagementPageSkeleton variant="cards" withAction={hasPermission(Permissions.StoresRolesCreate)} />
      </Box>
    );
  }

  if (error && !roles.length) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadRoles}>
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      {hasPermission(Permissions.StoresRolesCreate) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4, pt: 1 }}>
          <PrimaryActionButton startIcon={<AddIcon />} onClick={handleCreate}>
            {t('roles.addNew')}
          </PrimaryActionButton>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {roles.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            {t('roles.noRoles')}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {roles.map((role) => {
            const roleName = getRoleName(role.name, i18n.language);
            const permissionCount = role.permissions
              ? Array.isArray(role.permissions) ? role.permissions.length : Object.keys(role.permissions).length
              : 0;

            return (
              <Grid item xs={12} sm={6} md={4} key={role.id}>
                <Card
                  onClick={() => hasPermission(Permissions.StoresRolesUpdate) && handleEdit(role)}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    cursor: hasPermission(Permissions.StoresRolesUpdate) ? 'pointer' : 'default',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {roleName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('roles.card.permissions')}: {permissionCount}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        pt: 2,
                        borderTop: '1px solid #e0e0e0',
                        mt: 'auto',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {hasPermission(Permissions.StoresRolesUpdate) && (
                          <Tooltip title={t('roles.actions.edit')}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(role);
                              }}
                              sx={{ color: '#1976d2' }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission(Permissions.StoresRolesDelete) && (
                          <Tooltip title={t('roles.actions.delete')}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(role);
                              }}
                              sx={{ color: '#f44336' }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <RoleEditModal
        open={editModalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        role={selectedRole}
        storeId={storeId}
        isCreating={isCreating}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title={t('roles.delete.title')}
        message={selectedRole ? t('roles.delete.message', { name: getRoleName(selectedRole.name, i18n.language) }) : ''}
        confirmText={t('roles.delete.confirm')}
        cancelText={t('roles.delete.cancel')}
        confirmColor="error"
      />
    </Box>
  );
};

export default RolesPanel;
